import React, { useState, useEffect } from "react";
import { DailogBox } from "../../../../../components/Elements/DailogBox/DailogBox";
import CustomButton from "../../../../../components/Elements/Buttons/CustomButtons";
import { Input, Select } from "antd";
import { postApi } from "../../../../../api/api_instance";
import { API_URLS } from "../../../../../api/apiUrls";
import PopupCard from "../../../../../components/Cards/PopupCard/PopupCard"; // adjust if needed

const roleOptionsStatic = [
  { label: "Employee", value: "employee" },
  { label: "User", value: "user" },
  { label: "Vendor", value: "vendor" },
];

const CustomerAddUser = ({ onOpen, onClose, clientId, onSave }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState([]);
  const [location, setLocation] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const [errorMessage, setErrorMessage] = useState({
    name: "",
    email: "",
    role: "",
    password: "",
    confirmPassword: "",
  });

  const [popup, setPopup] = useState({
    visible: false,
    type: "success", // success | error | warning
    title: "",
    message: "",
  });

  useEffect(() => {
    if (!popup.visible) return;

    const timer = setTimeout(() => {
      setPopup((p) => ({ ...p, visible: false }));
    }, 2000);

    return () => clearTimeout(timer);
  }, [popup.visible]);

  useEffect(() => {
    // reset when opened
    if (onOpen) {
      setName("");
      setEmail("");
      setRole([]);
      setLocation("");
      setPassword("");
      setConfirmPassword("");
      setErrorMessage({
        name: "",
        email: "",
        role: "",
        password: "",
        confirmPassword: "",
      });
    }
  }, [onOpen]);

  const validate = () => {
    let isValid = true;
    const errors = {
      name: "",
      email: "",
      role: "",
      password: "",
      confirmPassword: "",
    };

    if (!name) {
      errors.name = "Name is required";
      isValid = false;
    }
    if (!email) {
      errors.email = "Email is required";
      isValid = false;
    }
    if (!role?.length) {
      errors.role = "At least one role is required";
      isValid = false;
    }
    if (!password) {
      errors.password = "Password is required";
      isValid = false;
    }
    if (!confirmPassword) {
      errors.confirmPassword = "Confirm Password is required";
      isValid = false;
    }
    if (password && confirmPassword && password !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
      isValid = false;
    }

    setErrorMessage(errors);
    return isValid;
  };

  const handleSave = async () => {
    if (!validate()) {
      setPopup({
        visible: true,
        type: "error",
        title: "Validation Error",
        message: "Please fix the highlighted fields before saving.",
      });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name,
        email,
        role, // array of values
        location: location || "",
      };

      const response = await postApi(
        {
          apiUrl: API_URLS.ADD_CUSTOMER_USER,
          headers: { client: clientId },
          data: payload,
        },
        true
      );

      if (response?.success) {
        setPopup({
          visible: true,
          type: "success",
          title: "User Created",
          message: "The new user has been successfully created.",
        });
        if (onSave) onSave();
      } else {
        setPopup({
          visible: true,
          type: "error",
          title: "Creation Failed",
          message: response?.message || "Failed to create user.",
        });
      }
    } catch (error) {
      setPopup({
        visible: true,
        type: "error",
        title: "Error",
        message:
          error?.response?.data?.message ||
          error.message ||
          "Something went wrong.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setErrorMessage({
      name: "",
      email: "",
      role: "",
      password: "",
      confirmPassword: "",
    });
    onClose();
  };

  const closePopup = () => {
    setPopup((p) => ({ ...p, visible: false }));
    // if success, optionally close dialog after a short delay
  };

  return (
    <>
      <DailogBox open={onOpen} onCancel={handleCancel} width="90%" maxWidth="800px">
        <div>
          <h3 className="text-xl font-bold text-[#06513D]">Add New User</h3>
        </div>

        <div className="rounded-xl border mt-8 px-12">
          <div className="mt-3 mb-4">
            <hr />
          </div>

          <div className="flex flex-col gap-6">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name"
              className="h-14 rounded-lg"
            />
            {errorMessage.name && (
              <div className="text-red-500 text-sm">{errorMessage.name}</div>
            )}

            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className="h-14 rounded-lg"
            />
            {errorMessage.email && (
              <div className="text-red-500 text-sm">{errorMessage.email}</div>
            )}
            {/* 
            <Input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Location"
              className="h-14 rounded-lg"
            /> */}

            <Input.Password
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="h-14 rounded-lg"
            />
            {errorMessage.password && (
              <div className="text-red-500 text-sm">
                {errorMessage.password}
              </div>
            )}

            <Input.Password
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm Password"
              className="h-14 rounded-lg"
            />
            {errorMessage.confirmPassword && (
              <div className="text-red-500 text-sm">
                {errorMessage.confirmPassword}
              </div>
            )}

            <Select
              mode="multiple"
              placeholder="Select Role(s)"
              options={roleOptionsStatic}
              value={role}
              onChange={setRole}
              style={{ minHeight: "40px" }}
              className="rounded-lg"
            />
            {errorMessage.role && (
              <div className="text-red-500 text-sm">{errorMessage.role}</div>
            )}
          </div>

          <div className="flex justify-center gap-6 p-5">
            <CustomButton
              buttonType="button"
              variant="cancel"
              buttonText="Cancel"
              onClick={handleCancel}
              className="text-base font-normal h-[50px] w-[160px]"
              disabled={loading}
            />

            <CustomButton
              buttonType="button"
              variant="ok"
              buttonText={loading ? "Saving..." : "Save"}
              onClick={handleSave}
              className="text-base font-normal h-[50px] w-[160px]"
              disabled={loading}
            />
          </div>
        </div>
      </DailogBox>

      {popup.visible && (
        <div
          className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/30"
          onClick={closePopup}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <PopupCard
              type={popup.type}
              title={popup.title}
              message={popup.message}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default CustomerAddUser;
